import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  closestCorners,
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Search, X, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LeadCard } from "./LeadCard";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { LeadTag } from "@/types/lead";
import { isHumanHandoffVisualStage, buildStageHandoffMap } from "@/lib/lead-stage-agent-handoff";

const ORDER_GAP = 1000;

interface Lead {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  stage: string;
  notes?: string;
  assigned_agent_id?: string;
  created_at: string;
  order_index?: number;
  tags?: LeadTag[];
  channel?: string | null;
  current_appointment_id?: string;
  current_appointment_date?: string;
}

interface Stage {
  id: string;
  name: string;
  slug: string;
  color: string;
  order_index: number;
  auto_disable_agent?: boolean;
}

/** Ordenação dentro de cada coluna do Kanban (dados já no cliente). */
function compareLeadsKanban(
  a: Lead,
  b: Lead,
  sortOption: string,
  unreadCountsMap?: Map<string, number>,
  unreadLastMessageAtMap?: Map<string, string>,
): number {
  const tieId = String(a.id || "").localeCompare(String(b.id || ""));

  const aUnread = (unreadCountsMap?.get(a.id) ?? 0) > 0;
  const bUnread = (unreadCountsMap?.get(b.id) ?? 0) > 0;
  if (aUnread !== bUnread) return aUnread ? -1 : 1;
  if (aUnread && bUnread) {
    const aMs = unreadLastMessageAtMap?.get(a.id)
      ? new Date(unreadLastMessageAtMap.get(a.id)!).getTime()
      : 0;
    const bMs = unreadLastMessageAtMap?.get(b.id)
      ? new Date(unreadLastMessageAtMap.get(b.id)!).getTime()
      : 0;
    if (aMs !== bMs) return bMs - aMs;
  }

  switch (sortOption) {
    case "az": {
      const c = String(a.name ?? "").localeCompare(String(b.name ?? ""), "pt-BR", {
        sensitivity: "base",
      });
      return c !== 0 ? c : tieId;
    }
    case "za": {
      const c = String(b.name ?? "").localeCompare(String(a.name ?? ""), "pt-BR", {
        sensitivity: "base",
      });
      return c !== 0 ? c : tieId;
    }
    // recent / oldest / stage: no Kanban mantemos order_index (igual ao RPC + arrastar).
    // Data “mais recentes” na grade/lista continua vindo do servidor em Leads.tsx.
    case "recent":
    case "oldest":
    case "stage":
    default: {
      const oi = (a.order_index || 0) - (b.order_index || 0);
      if (oi !== 0) return oi;
      const ct =
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return ct !== 0 ? ct : tieId;
    }
  }
}

function normalizeSearchToken(s: string): string {
  return s.trim().toLowerCase();
}

function leadMatchesColumnSearch(lead: Lead, rawQuery: string): boolean {
  const q = rawQuery.trim();
  if (!q) return true;
  const tokens = q.split(/\s+/).map(normalizeSearchToken).filter(Boolean);
  if (tokens.length === 0) return true;

  const name = normalizeSearchToken(lead.name ?? "");

  return tokens.every(
    (token) =>
      name.includes(token) ||
      (lead.tags ?? []).some((t) => normalizeSearchToken(t.name).includes(token)),
  );
}

function isKanbanReorderDisabled(sortOption: string, columnQuery: string): boolean {
  if (columnQuery.trim().length > 0) return true;
  return sortOption === "az" || sortOption === "za";
}

function getLeadsForStage(
  allLeads: Lead[],
  stageSlug: string,
  sortOption: string,
  validStageSlugs: Set<string>,
  firstStageSlug: string | null,
  unreadCountsMap?: Map<string, number>,
  unreadLastMessageAtMap?: Map<string, string>,
): Lead[] {
  const directMatches = allLeads.filter((lead) => lead.stage === stageSlug);
  let base: Lead[];
  if (stageSlug === firstStageSlug) {
    const orphans = allLeads.filter((lead) => !validStageSlugs.has(lead.stage));
    base = [...directMatches, ...orphans];
  } else {
    base = directMatches;
  }
  return [...base].sort((a, b) =>
    compareLeadsKanban(a, b, sortOption, unreadCountsMap, unreadLastMessageAtMap),
  );
}

function assignStageOrderIndexes(
  leads: Lead[],
  stageSlug: string,
  orderedIds: string[],
): Lead[] {
  const orderById = new Map(orderedIds.map((id, idx) => [id, (idx + 1) * ORDER_GAP]));
  return leads.map((lead) => {
    const nextOrder = orderById.get(lead.id);
    if (nextOrder === undefined) return lead;
    return { ...lead, order_index: nextOrder };
  });
}

interface LeadsKanbanProps {
  workspaceId: string;
  leads: Lead[];
  sortOption?: string;
  /** Atualiza contadores de coluna após mover estágio (sem refetch da lista). */
  onLeadMoveCommitted?: (fromStage: string, toStage: string) => void;
  onOptimisticUpdate: (leadId: string, patch: { stage?: string; order_index?: number }) => void;
  onLeadClick: (lead: Lead) => void;
  onOpenLeadChat?: (lead: Lead) => void;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectLeadsInStage?: (
    stageSlug: string,
    visibleIds: string[],
    options: { totalCount: number; searchActive: boolean },
  ) => void | Promise<void>;
  fullySelectedStageSlugs?: Set<string>;
  selectingStageSlug?: string | null;
  primaryGroupsMap?: Map<string, { name: string; role?: string }>;
  assignedMembersMap?: Map<string, string>;
  closedLeadsSet?: Set<string>;
  unreadCountsMap?: Map<string, number>;
  unreadLastMessageAtMap?: Map<string, string>;
  activeFunnelId?: string | null;
  isIntegrationFunnel?: boolean;
  stageCounts?: Record<string, number>;
  onLoadMoreStage?: (stageSlug: string) => void;
  scrollToStageSlug?: string | null;
  onScrollToStageConsumed?: () => void;
}

function SortableLeadCard({
  lead,
  onLeadClick,
  onOpenLeadChat,
  selectionMode,
  isSelected,
  onToggleSelect,
  primaryGroup,
  assignedMemberName,
  isClosed,
  unreadCount,
  dragDisabled,
  handoffStagesMap,
  kanbanMobile,
  moveStages,
  onMoveToStage,
}: {
  lead: Lead;
  onLeadClick: (lead: Lead) => void;
  onOpenLeadChat?: (lead: Lead) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  primaryGroup?: { name: string; role?: string } | null;
  assignedMemberName?: string | null;
  isClosed?: boolean;
  unreadCount?: number;
  dragDisabled?: boolean;
  handoffStagesMap?: Map<string, { slug: string; name: string; auto_disable_agent: boolean }>;
  kanbanMobile?: boolean;
  moveStages?: { slug: string; name: string; color: string }[];
  onMoveToStage?: (lead: Lead, stageSlug: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: lead.id, disabled: Boolean(dragDisabled) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {isOver && (
        <div className="h-0.5 bg-primary rounded-full mx-2 mb-1 animate-in fade-in duration-200" />
      )}
      <div className={cn(isDragging && "opacity-30")}>
        <LeadCard
          lead={lead}
          onClick={() => onLeadClick(lead)}
          onChatClick={onOpenLeadChat ? () => onOpenLeadChat(lead) : undefined}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
          tags={lead.tags}
          primaryGroup={primaryGroup}
          assignedMemberName={assignedMemberName}
          isClosed={isClosed}
          unreadCount={unreadCount}
          sortableProps={dragDisabled ? undefined : { ...attributes, ...listeners }}
          handoffStagesMap={handoffStagesMap}
          kanbanMobile={kanbanMobile}
          moveStages={moveStages}
          onMoveToStage={onMoveToStage}
        />
      </div>
    </div>
  );
}

function LoadMoreSentinel({ onLoadMore }: { onLoadMore: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore]);

  return <div ref={ref} className="h-1" />;
}

function DroppableColumn({
  stage,
  leads,
  sortedLoadedCount,
  totalCount,
  onLeadClick,
  onOpenLeadChat,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectLeadsInStage,
  fullySelectedStageSlugs,
  selectingStageSlug,
  primaryGroupsMap,
  assignedMembersMap,
  closedLeadsSet,
  unreadCountsMap,
  onLoadMore,
  hasMore,
  columnSearch,
  onColumnSearchChange,
  dragDisabled,
  stageHandoffMap,
  hideHeader,
  className,
  kanbanMobile,
  moveStages,
  onMoveToStage,
}: {
  stage: Stage;
  leads: Lead[];
  sortedLoadedCount: number;
  totalCount: number;
  onLeadClick: (lead: Lead) => void;
  onOpenLeadChat?: (lead: Lead) => void;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectLeadsInStage?: (
    stageSlug: string,
    visibleIds: string[],
    options: { totalCount: number; searchActive: boolean },
  ) => void | Promise<void>;
  fullySelectedStageSlugs?: Set<string>;
  selectingStageSlug?: string | null;
  primaryGroupsMap?: Map<string, { name: string; role?: string }>;
  assignedMembersMap?: Map<string, string>;
  closedLeadsSet?: Set<string>;
  unreadCountsMap?: Map<string, number>;
  onLoadMore: () => void;
  hasMore: boolean;
  columnSearch: string;
  onColumnSearchChange: (value: string) => void;
  dragDisabled: boolean;
  stageHandoffMap: Map<string, { slug: string; name: string; auto_disable_agent: boolean }>;
  hideHeader?: boolean;
  className?: string;
  kanbanMobile?: boolean;
  moveStages?: { slug: string; name: string; color: string }[];
  onMoveToStage?: (lead: Lead, stageSlug: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const columnIds = leads.map((l) => l.id);
  const selectedInColumn = columnIds.filter((id) => selectedIds?.has(id)).length;
  const searchActive = columnSearch.trim().length > 0;
  const allInStageSelected =
    totalCount > 0 &&
    (fullySelectedStageSlugs?.has(stage.slug) ||
      (columnIds.length >= totalCount &&
        columnIds.length > 0 &&
        selectedInColumn === columnIds.length));
  const columnChecked = allInStageSelected
    ? true
    : selectedInColumn > 0
      ? ("indeterminate" as const)
      : false;
  const isSelectingColumn = selectingStageSlug === stage.slug;
  const noMatches =
    leads.length === 0 && searchActive && sortedLoadedCount > 0;
  const emptyColumn = leads.length === 0 && !searchActive && sortedLoadedCount === 0;
  const remaining = Math.max(0, totalCount - sortedLoadedCount);

  return (
    <Card
      ref={setNodeRef}
      id={`kanban-stage-${stage.slug}`}
      className={cn(
        "flex flex-col h-full min-h-0 self-stretch transition-colors w-[280px] flex-shrink-0",
        isOver && "ring-2 ring-primary",
        isHumanHandoffVisualStage(stage.slug, stageHandoffMap) && !hideHeader && "border-2 border-orange-500",
        className,
      )}
    >
      {!hideHeader && (
      <CardHeader className="pb-3 flex-shrink-0 px-4 pt-4 border-b">
        <CardTitle className="text-sm font-medium flex items-center gap-2 w-full min-w-0">
          {selectionMode ? (
            isSelectingColumn ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
            ) : (
              <Checkbox
                checked={columnChecked}
                onCheckedChange={() =>
                  onToggleSelectLeadsInStage?.(stage.slug, columnIds, {
                    totalCount,
                    searchActive,
                  })
                }
                onClick={(e) => e.stopPropagation()}
                aria-label={
                  searchActive
                    ? `Selecionar todos os leads visíveis em ${stage.name}`
                    : `Selecionar todos os ${totalCount} leads em ${stage.name}`
                }
                className="shrink-0"
                disabled={isSelectingColumn}
              />
            )
          ) : null}
          <span className="flex items-center gap-2 min-w-0 flex-1">
            <div className={`w-2 h-2 rounded-full ${stage.color} flex-shrink-0`} />
            <span className="truncate">{stage.name}</span>
          </span>
          <span className="flex items-center gap-0.5 shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Buscar nesta coluna por nome ou tag"
                >
                  <Search
                    className={cn("h-4 w-4", searchActive && "text-primary")}
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end" sideOffset={8}>
                <label className="text-xs font-medium text-muted-foreground sr-only">
                  Buscar nesta coluna
                </label>
                <div className="flex gap-1">
                  <Input
                    placeholder="Nome ou tag…"
                    value={columnSearch}
                    onChange={(e) => onColumnSearchChange(e.target.value)}
                    className="h-9 text-sm"
                    autoComplete="off"
                  />
                  {searchActive ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      aria-label="Limpar busca"
                      onClick={() => onColumnSearchChange("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
                {searchActive && sortedLoadedCount > 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">
                    {leads.length} de {sortedLoadedCount} carregados nesta coluna
                  </p>
                ) : null}
                <p className="text-xs text-muted-foreground mt-2">
                  A busca usa só os leads já carregados neste estágio.
                  {hasMore && searchActive ? (
                    <span> Use “Mostrar mais” para carregar mais do funil.</span>
                  ) : null}
                </p>
              </PopoverContent>
            </Popover>
            <Badge
              variant={
                isHumanHandoffVisualStage(stage.slug, stageHandoffMap) ? "destructive" : "secondary"
              }
              className="text-xs flex-shrink-0"
            >
              {totalCount}
            </Badge>
          </span>
        </CardTitle>
      </CardHeader>
      )}
      <CardContent className={cn("flex-1 overflow-y-auto min-h-0 space-y-2", hideHeader ? "p-2" : "px-4 py-4")}>
        <SortableContext
          items={leads.map((l) => l.id)}
          strategy={verticalListSortingStrategy}
        >
          {leads.map((lead) => (
            <SortableLeadCard
              key={lead.id}
              lead={lead}
              onLeadClick={onLeadClick}
              onOpenLeadChat={onOpenLeadChat}
              selectionMode={selectionMode}
              isSelected={selectedIds?.has(lead.id)}
              onToggleSelect={onToggleSelect}
              primaryGroup={primaryGroupsMap?.get(lead.id)}
              assignedMemberName={assignedMembersMap?.get(lead.id)}
              isClosed={closedLeadsSet?.has(lead.id)}
              unreadCount={unreadCountsMap?.get(lead.id)}
              dragDisabled={dragDisabled}
              handoffStagesMap={stageHandoffMap}
              kanbanMobile={kanbanMobile}
              moveStages={moveStages}
              onMoveToStage={onMoveToStage}
            />
          ))}
        </SortableContext>
        {noMatches && (
          <>
            {isOver && (
              <div className="h-0.5 bg-primary rounded-full mx-2 mb-1 animate-in fade-in duration-200" />
            )}
            <p className="text-sm text-muted-foreground text-center py-6 px-1">
              Nenhum lead corresponde à busca nesta coluna.
            </p>
          </>
        )}
        {emptyColumn && (
          <>
            {isOver && (
              <div className="h-0.5 bg-primary rounded-full mx-2 mb-1 animate-in fade-in duration-200" />
            )}
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum lead neste estágio
            </p>
          </>
        )}
        {hasMore && (
          <>
            <LoadMoreSentinel onLoadMore={onLoadMore} />
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={onLoadMore}
            >
              Mostrar mais ({remaining} restantes)
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function LeadsKanban({
  workspaceId,
  leads,
  sortOption = "recent",
  onLeadMoveCommitted,
  onOptimisticUpdate,
  onLeadClick,
  onOpenLeadChat,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectLeadsInStage,
  fullySelectedStageSlugs,
  selectingStageSlug,
  primaryGroupsMap,
  assignedMembersMap,
  closedLeadsSet,
  unreadCountsMap,
  unreadLastMessageAtMap,
  activeFunnelId,
  isIntegrationFunnel,
  stageCounts,
  onLoadMoreStage,
  scrollToStageSlug,
  onScrollToStageConsumed,
}: LeadsKanbanProps) {
  const [stages, setStages] = useState<Stage[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragLeads, setDragLeads] = useState<Lead[] | null>(null);
  const [columnQueries, setColumnQueries] = useState<Record<string, string>>({});
  const [mobileStageSlug, setMobileStageSlug] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  useEffect(() => {
    fetchStages();
  }, [workspaceId, activeFunnelId]);

  useEffect(() => {
    setColumnQueries({});
  }, [workspaceId, activeFunnelId]);

  useEffect(() => {
    if (stages.length === 0) {
      setMobileStageSlug(null);
      return;
    }
    if (!mobileStageSlug || !stages.some((s) => s.slug === mobileStageSlug)) {
      setMobileStageSlug(stages[0].slug);
    }
  }, [stages, mobileStageSlug]);

  useEffect(() => {
    if (!scrollToStageSlug || stages.length === 0) return;
    if (isMobile) {
      setMobileStageSlug(scrollToStageSlug);
      onScrollToStageConsumed?.();
      return;
    }
    const el = document.getElementById(`kanban-stage-${scrollToStageSlug}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
    onScrollToStageConsumed?.();
  }, [scrollToStageSlug, stages, onScrollToStageConsumed, isMobile]);

  const fetchStages = async () => {
    try {
      let query = supabase
        .from("lead_stages")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true });

      if (activeFunnelId) {
        query = query.eq("funnel_id", activeFunnelId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setStages(data || []);
    } catch (error: any) {
      console.error("Error fetching stages:", error);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const validStageSlugs = useMemo(() => new Set(stages.map((s) => s.slug)), [stages]);
  const firstStageSlug = stages.length > 0 ? stages[0].slug : null;
  const stageHandoffMap = useMemo(
    () =>
      buildStageHandoffMap(
        stages.map((s) => ({
          slug: s.slug,
          name: s.name,
          auto_disable_agent: s.auto_disable_agent ?? false,
        })),
      ),
    [stages],
  );

  const getStageLeads = useCallback(
    (source: Lead[], stageSlug: string) =>
      getLeadsForStage(
        source,
        stageSlug,
        sortOption,
        validStageSlugs,
        firstStageSlug,
        unreadCountsMap,
        unreadLastMessageAtMap,
      ),
    [sortOption, validStageSlugs, firstStageSlug, unreadCountsMap, unreadLastMessageAtMap],
  );

  const effectiveLeads = dragLeads ?? leads;

  const resolveStageSlug = useCallback(
    (id: string, source: Lead[]): string | null => {
      const stageMatch = stages.find((s) => s.id === id);
      if (stageMatch) return stageMatch.slug;
      return source.find((l) => l.id === id)?.stage ?? null;
    },
    [stages],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setDragLeads([...leads]);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !dragLeads) return;

    const activeLeadId = String(active.id);
    const overId = String(over.id);

    setDragLeads((prev) => {
      if (!prev) return prev;

      const activeLead = prev.find((l) => l.id === activeLeadId);
      if (!activeLead) return prev;

      const targetStageSlug = resolveStageSlug(overId, prev);
      if (!targetStageSlug) return prev;

      const isOverColumn = stages.some((s) => s.id === overId);
      const withStageUpdate = prev.map((l) =>
        l.id === activeLeadId ? { ...l, stage: targetStageSlug } : l,
      );
      const stageLeads = getStageLeads(withStageUpdate, targetStageSlug);

      const oldIndex = stageLeads.findIndex((l) => l.id === activeLeadId);
      if (oldIndex < 0) return prev;

      let newIndex: number;
      if (isOverColumn) {
        newIndex = stageLeads.length - 1;
      } else {
        const overIndex = stageLeads.findIndex((l) => l.id === overId);
        if (overIndex < 0) return prev;
        newIndex = overIndex;
      }

      if (oldIndex === newIndex && activeLead.stage === targetStageSlug) return prev;

      const reorderedIds = arrayMove(
        stageLeads.map((l) => l.id),
        oldIndex,
        newIndex,
      );

      return assignStageOrderIndexes(withStageUpdate, targetStageSlug, reorderedIds).map((l) =>
        l.id === activeLeadId ? { ...l, stage: targetStageSlug } : l,
      );
    });
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveId(null);
    setDragLeads(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const workingLeads = dragLeads ?? leads;

    if (!over) {
      setDragLeads(null);
      setActiveId(null);
      return;
    }

    const leadId = active.id as string;
    const originalLead = leads.find((l) => l.id === leadId);
    const lead = workingLeads.find((l) => l.id === leadId);
    if (!lead || !originalLead) {
      setDragLeads(null);
      setActiveId(null);
      return;
    }

    const isStageId = stages.some((s) => s.id === over.id);
    const overLeadId = isStageId ? null : (over.id as string);
    let targetStageSlug = lead.stage;

    if (isStageId) {
      targetStageSlug = stages.find((s) => s.id === over.id)?.slug || lead.stage;
    } else {
      const overLead = workingLeads.find((l) => l.id === over.id);
      targetStageSlug = overLead?.stage || lead.stage;
    }

    const previousStage = originalLead.stage;
    const previousOrderIndex = originalLead.order_index ?? 0;

    let stageItems = getStageLeads(
      workingLeads.map((l) => (l.id === leadId ? { ...l, stage: targetStageSlug } : l)),
      targetStageSlug,
    );

    const activeIndex = stageItems.findIndex((l) => l.id === leadId);
    if (activeIndex < 0) {
      stageItems = [...stageItems, { ...lead, stage: targetStageSlug }];
    }

    if (overLeadId) {
      const overIndex = stageItems.findIndex((l) => l.id === overLeadId);
      const fromIndex = stageItems.findIndex((l) => l.id === leadId);
      if (overIndex >= 0 && fromIndex >= 0 && fromIndex !== overIndex) {
        stageItems = arrayMove(stageItems, fromIndex, overIndex);
      }
    } else if (isStageId) {
      const fromIndex = stageItems.findIndex((l) => l.id === leadId);
      if (fromIndex >= 0 && fromIndex !== stageItems.length - 1) {
        stageItems = arrayMove(stageItems, fromIndex, stageItems.length - 1);
      }
    }

    const orderById = new Map(
      stageItems.map((item, idx) => [item.id, (idx + 1) * ORDER_GAP]),
    );
    const newOrderIndex = orderById.get(leadId) ?? lead.order_index ?? ORDER_GAP;
    const stageChanged = previousStage !== targetStageSlug;
    const orderChanged = previousOrderIndex !== newOrderIndex;

    if (!stageChanged && !orderChanged) {
      setDragLeads(null);
      setActiveId(null);
      return;
    }

    // Sincroniza estado pai antes de limpar dragLeads (evita snap-back visual)
    const persistedUpdates: Array<{ id: string; stage?: string; order_index: number }> = [];
    for (const item of stageItems) {
      const idx = orderById.get(item.id);
      if (idx == null) continue;
      const orig = leads.find((l) => l.id === item.id);
      const stagePatch = item.id === leadId && stageChanged ? targetStageSlug : undefined;
      const orderPatch = (orig?.order_index ?? 0) !== idx;
      if (!stagePatch && !orderPatch) continue;

      onOptimisticUpdate(item.id, {
        ...(stagePatch ? { stage: stagePatch } : {}),
        order_index: idx,
      });
      persistedUpdates.push({
        id: item.id,
        ...(stagePatch ? { stage: stagePatch } : {}),
        order_index: idx,
      });
    }

    setDragLeads(null);
    setActiveId(null);

    if (stageChanged) {
      onLeadMoveCommitted?.(previousStage, targetStageSlug);
    }

    try {
      for (const patch of persistedUpdates) {
        if (patch.stage) {
          // RPC decide: funil principal atualiza o lead; outro funil atualiza só a coluna naquele funil.
          const { error } = await supabase.rpc("move_lead_to_stage", {
            p_lead_id: patch.id,
            p_stage: patch.stage,
            p_funnel_id: activeFunnelId ?? null,
            p_order_index: patch.order_index,
          });
          if (error) throw error;
        } else if (activeFunnelId) {
          const { error } = await supabase.rpc("reorder_lead_in_funnel", {
            p_lead_id: patch.id,
            p_funnel_id: activeFunnelId,
            p_order_index: patch.order_index,
          });
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("leads")
            .update({ order_index: patch.order_index })
            .eq("id", patch.id);
          if (error) throw error;
        }
      }
    } catch (error: any) {
      for (const item of stageItems) {
        const orig = leads.find((l) => l.id === item.id);
        if (!orig) continue;
        onOptimisticUpdate(item.id, {
          stage: orig.stage,
          order_index: orig.order_index ?? 0,
        });
      }
      if (stageChanged) {
        onLeadMoveCommitted?.(targetStageSlug, previousStage);
      }
      toast({
        title: "Erro ao atualizar lead",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getSortedLeadsForStage = useCallback(
    (slug: string) => getStageLeads(effectiveLeads, slug),
    [effectiveLeads, getStageLeads],
  );

  const setColumnQuery = useCallback((slug: string, value: string) => {
    setColumnQueries((prev) => ({ ...prev, [slug]: value }));
  }, []);

  const handleMoveToStage = useCallback(
    (lead: Lead, stageSlug: string) => {
      if (lead.stage === stageSlug) return;
      const targetList = getSortedLeadsForStage(stageSlug);
      const lastOrder = targetList[targetList.length - 1]?.order_index ?? 0;
      const orderIndex = lastOrder + ORDER_GAP;
      const previousStage = lead.stage;
      onOptimisticUpdate(lead.id, { stage: stageSlug, order_index: orderIndex });
      setMobileStageSlug(stageSlug);
      onLeadMoveCommitted?.(previousStage, stageSlug);
      void supabase
        .rpc("move_lead_to_stage", {
          p_lead_id: lead.id,
          p_stage: stageSlug,
          p_funnel_id: activeFunnelId ?? null,
          p_order_index: orderIndex,
        })
        .then(({ error }) => {
          if (!error) return;
          onOptimisticUpdate(lead.id, {
            stage: previousStage,
            order_index: lead.order_index ?? 0,
          });
          onLeadMoveCommitted?.(stageSlug, previousStage);
          toast({
            title: "Erro ao mover lead",
            description: error.message,
            variant: "destructive",
          });
        });
    },
    [
      getSortedLeadsForStage,
      onOptimisticUpdate,
      onLeadMoveCommitted,
      activeFunnelId,
      toast,
    ],
  );

  const renderColumn = (stage: Stage) => {
    const sortedLeads = getSortedLeadsForStage(stage.slug);
    const q = columnQueries[stage.slug] ?? "";
    const displayLeads = sortedLeads.filter((l) => leadMatchesColumnSearch(l, q));
    const serverTotal = stageCounts?.[stage.slug];
    const totalCount = serverTotal != null ? serverTotal : sortedLeads.length;
    const hasMore = sortedLeads.length < totalCount;
    const dragDisabled =
      isMobile || Boolean(selectionMode) || isKanbanReorderDisabled(sortOption, q);
    const moveStages = stages
      .filter((s) => s.slug !== stage.slug)
      .map((s) => ({ slug: s.slug, name: s.name, color: s.color }));

    return (
      <DroppableColumn
        key={stage.id}
        stage={stage}
        leads={displayLeads}
        sortedLoadedCount={sortedLeads.length}
        totalCount={totalCount}
        onLeadClick={onLeadClick}
        onOpenLeadChat={onOpenLeadChat}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
        onToggleSelectLeadsInStage={onToggleSelectLeadsInStage}
        fullySelectedStageSlugs={fullySelectedStageSlugs}
        selectingStageSlug={selectingStageSlug}
        primaryGroupsMap={primaryGroupsMap}
        assignedMembersMap={assignedMembersMap}
        closedLeadsSet={closedLeadsSet}
        unreadCountsMap={unreadCountsMap}
        onLoadMore={() => onLoadMoreStage?.(stage.slug)}
        hasMore={hasMore}
        columnSearch={q}
        onColumnSearchChange={(v) => setColumnQuery(stage.slug, v)}
        dragDisabled={dragDisabled}
        stageHandoffMap={stageHandoffMap}
        hideHeader={isMobile}
        className={
          isMobile ? "w-full min-w-0 border-0 shadow-none bg-transparent" : undefined
        }
        kanbanMobile={isMobile}
        moveStages={isMobile ? moveStages : undefined}
        onMoveToStage={isMobile ? handleMoveToStage : undefined}
      />
    );
  };

  const activeLead = effectiveLeads.find((lead) => lead.id === activeId);

  if (stages.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Carregando estágios do pipeline...
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden">
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {isMobile ? (
            <div className="flex flex-col flex-1 min-h-0">
              <div
                className="flex gap-1.5 overflow-x-auto py-1 shrink-0 snap-x snap-mandatory"
                role="tablist"
                aria-label="Estágios do funil"
              >
                {stages.map((stage) => {
                  const count = stageCounts?.[stage.slug] ?? getSortedLeadsForStage(stage.slug).length;
                  const active = stage.slug === mobileStageSlug;
                  return (
                    <button
                      key={stage.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setMobileStageSlug(stage.slug)}
                      className={cn(
                        "snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 h-8 text-xs font-medium transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        active ? "bg-primary/10 text-foreground" : "text-muted-foreground",
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", stage.color)} />
                      <span className="max-w-[9rem] truncate">{stage.name}</span>
                      <span className="tabular-nums text-[10px] opacity-70">{count}</span>
                    </button>
                  );
                })}
              </div>
              {stages.filter((stage) => stage.slug === mobileStageSlug).map(renderColumn)}
            </div>
          ) : (
            <div className="flex flex-1 min-h-0 h-full items-stretch gap-3 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
              {stages.map(renderColumn)}
            </div>
          )}

          <DragOverlay>
            {activeLead ? (
              <div className="rotate-3 opacity-80">
                <LeadCard
                  lead={activeLead}
                  onClick={() => {}}
                  assignedMemberName={assignedMembersMap?.get(activeLead.id)}
                  unreadCount={unreadCountsMap?.get(activeLead.id)}
                  handoffStagesMap={stageHandoffMap}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
